import React, { useState, useEffect } from "react";
import { Box, Card, IconButton, Icon, Tabs, Tab } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import { get, postNoResponse } from "utils/WebService";
import { useUser } from "context/UserContext";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, MenuItem, CircularProgress } from "@mui/material";
import MDInput from "components/MDInput";
import MDSelect from "components/MDSelect";
import MDSnackbar from "components/MDSnackbar";
import MDButton from "components/MDButton";
import Contratos from "./Contratos";
import Historico from "./Historico";
import Acordos from "./Acordos";

export default function Honorarios({ formRef, recordId, isViewOnly, pendingContratos = [], setPendingContratos, changeTab, onDataUpdated, refreshKey, preloadedDevedor, preloadedContratos, preloadedAcordos, preloadedHistoricos }) {
    const [fichas, setFichas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeFichaId, setActiveFichaId] = useState(null);
    const [tabValue, setTabValue] = useState(1);
    const [visitedTabs, setVisitedTabs] = useState(new Set([1, 2, 3]));

    const handleChangeTab = (event, newValue) => {
        setTabValue(newValue);
    };
    const { userPerfils } = useUser();
    
    // Roles
    const isAdminOrGestor = Array.isArray(userPerfils) && userPerfils.some(p => p?.tipoPerfil === 4 || p?.tipoPerfil === 6 || p?.nome === 'Administrador' || p?.nome === 'Suporte' || p === 'Administrador' || p === 'Suporte' || p?.nome === 'Gestor' || p === 'Gestor');
    const isSupervisorOrCoordenador = Array.isArray(userPerfils) && userPerfils.some(p => p?.tipoPerfil === 3 || p?.nome === 'Supervisor' || p === 'Supervisor' || p?.nome === 'Coordenador' || p === 'Coordenador');
    const isOperador = !isAdminOrGestor && !isSupervisorOrCoordenador;

    // Edit Modal States
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [fichaToEdit, setFichaToEdit] = useState(null);
    const [editForm, setEditForm] = useState({ carteiraId: "", statusId: "", usuarioId: "" });
    const [carteirasOptions, setCarteirasOptions] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);
    const [usuariosOptions, setUsuariosOptions] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    
    // Snackbar States
    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);
    const [sbMessage, setSbMessage] = useState("");


    const loadFichas = () => {
        if (preloadedDevedor !== undefined) {
            if (preloadedDevedor && preloadedDevedor.fichas) {
                const honorarios = preloadedDevedor.fichas.filter(f => f.isHonorario === true);
                setFichas(honorarios);
            } else {
                setFichas([]);
            }
            setLoading(false);
            return;
        }

        setLoading(true);
        get(`/devedor/by-id/${recordId}`)
            .then(res => {
                if (res && res.fichas) {
                    const honorarios = res.fichas.filter(f => f.isHonorario === true);
                    setFichas(honorarios);
                }
            })
            .catch(err => console.error("Erro ao carregar fichas de honorários", err))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (recordId || preloadedDevedor) {
            loadFichas();
        }
    }, [recordId, refreshKey, preloadedDevedor]);

    const handleBackToGrid = () => {
        setActiveFichaId(null);
        setTabValue(1);
    };

    const handleOpenEdit = (ficha) => {
        setFichaToEdit(ficha);
        setEditForm({
            carteiraId: ficha.idCarteira || "",
            statusId: ficha.idStatusFicha || "",
            usuarioId: ficha.idUsuarioDono || ""
        });
        
        // Load Options if empty
        if (carteirasOptions.length === 0) {
            get("/carteira/list-all").then(res => {
                let list = res?.list || res?.data || (Array.isArray(res) ? res : []);
                setCarteirasOptions(list);
            }).catch(() => {});
        }
        if (statusOptions.length === 0) {
            get("/status-ficha/list-all").then(res => setStatusOptions(res.data || [])).catch(() => {});
        }
        if (usuariosOptions.length === 0) {
            get("/usuario/list-combo").then(res => setUsuariosOptions(res || [])).catch(() => {});
        }
        
        setEditModalOpen(true);
    };

    const handleSaveEdit = () => {
        setIsSaving(true);
        postNoResponse(`/devedor/carteira/update/${fichaToEdit.id}`, editForm)
            .then(() => {
                setSbMessage("Ficha atualizada com sucesso!");
                setSuccessSB(true);
                setEditModalOpen(false);
                if (onDataUpdated) onDataUpdated();
                else loadFichas();
            })
            .catch(() => {
                setSbMessage("Erro ao atualizar ficha.");
                setErrorSB(true);
            })
            .finally(() => setIsSaving(false));
    };

    const handleDelete = (id) => {
        if (window.confirm("Deseja realmente excluir esta ficha de honorários? Todos os contratos atrelados a ela também serão apagados. O acordo formalizado ficará sem os honorários.")) {
            postNoResponse(`/devedor/carteira/delete/${id}`, {})
                .then(() => {
                    setSbMessage("Ficha excluída com sucesso!");
                    setSuccessSB(true);
                    if (onDataUpdated) onDataUpdated();
                    else loadFichas();
                })
                .catch(() => {
                    setSbMessage("Erro ao excluir ficha.");
                    setErrorSB(true);
                });
        }
    };


    const columns = [
        { 
            header: "ID", 
            accessorKey: "rawId", 
            align: "center", 
            width: "10%" 
        },
        { 
            header: "Carteira", 
            accessorKey: "nomeCarteira", 
            align: "left",
            cell: ({ row }) => (
                <MDTypography variant="caption" color="text" fontWeight="medium">
                    {row.original.nomeCarteira || "Não informada"}
                </MDTypography>
            )
        },
        { 
            header: "Status", 
            accessorKey: "nomeStatusFicha", 
            align: "left",
            cell: ({ row }) => (
                <MDTypography variant="caption" color="text" fontWeight="medium">
                    {row.original.nomeStatusFicha || "Sem status"}
                </MDTypography>
            )
        },
        { 
            header: "Propriedade", 
            accessorKey: "nomeUsuarioDono", 
            align: "left",
            cell: ({ row }) => (
                <MDTypography variant="caption" color="text" fontWeight="medium">
                    {row.original.nomeUsuarioDono || "Sem responsável"}
                </MDTypography>
            )
        },
        {
            header: "Ações",
            accessorKey: "actions",
            align: "center",
            cell: ({ row }) => (
                <MDBox display="flex" justifyContent="center">
                    <IconButton onClick={() => setActiveFichaId(row.original.id)} color="info" size="small" title="Visualizar">
                        <Icon>visibility</Icon>
                    </IconButton>
                    <IconButton onClick={() => handleOpenEdit(row.original)} color="warning" size="small" title="Editar">
                        <Icon>edit</Icon>
                    </IconButton>
                    {!isOperador && (
                        <IconButton onClick={() => handleDelete(row.original.id)} color="error" size="small" title="Excluir">
                            <Icon>delete</Icon>
                        </IconButton>
                    )}
                </MDBox>
            )
        }
    ];

    if (activeFichaId) {
        return (
            <Card id="honorarios-ficha-detalhe" sx={{ boxShadow: "none" }}>
                <MDBox display="flex" alignItems="center" p={2}>
                    <MDButton variant="text" color="dark" onClick={handleBackToGrid}>
                        <Icon sx={{ mr: 1 }}>arrow_back</Icon> Voltar
                    </MDButton>
                </MDBox>
                
                <MDBox sx={{ width: '100%', px: 2, mb: 4 }}>
                    <MDBox sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={tabValue} onChange={handleChangeTab} aria-label="honorarios-tabs">
                            <Tab label="Contratos" value={1} sx={{ fontSize: 12 }} />
                            <Tab label="Histórico" value={2} sx={{ fontSize: 12 }} />
                            <Tab label="Acordos" value={3} sx={{ fontSize: 12 }} />
                        </Tabs>
                    </MDBox>

                    <MDBox mt={3}>
                        <div role="tabpanel" hidden={tabValue !== 1}>
                            {tabValue === 1 && (
                                <Contratos 
                                    formRef={formRef} 
                                    recordId={recordId} 
                                    idDevedorCarteira={activeFichaId}
                                    isViewOnly={isViewOnly} 
                                    pendingContratos={pendingContratos} 
                                    setPendingContratos={setPendingContratos} 
                                    changeTab={handleChangeTab} 
                                    onDataUpdated={onDataUpdated} 
                                    isHonorariosTab={true}
                                    preloadedContratos={preloadedContratos}
                                    preloadedAcordos={preloadedAcordos}
                                />
                            )}
                        </div>
                        <div role="tabpanel" hidden={tabValue !== 2}>
                            {tabValue === 2 && (
                                <Historico 
                                    recordId={recordId} 
                                    isViewOnly={isViewOnly} 
                                    idDevedorCarteira={activeFichaId} 
                                    preloadedDevedor={preloadedDevedor}
                                    preloadedHistoricos={preloadedHistoricos}
                                />
                            )}
                        </div>
                        <div role="tabpanel" hidden={tabValue !== 3}>
                            {tabValue === 3 && (
                                <Acordos 
                                    recordId={recordId} 
                                    isViewOnly={isViewOnly} 
                                    idDevedorCarteira={activeFichaId} 
                                    changeTab={handleChangeTab} 
                                    isHonorariosTab={true} 
                                    onDataUpdated={onDataUpdated} 
                                    preloadedAcordos={preloadedAcordos}
                                />
                            )}
                        </div>
                    </MDBox>
                </MDBox>
            </Card>
        );
    }

    return (
        <MDBox mt={2}>
            <DataTable
                columnData={columns}
                data={fichas}
                isSorted={false}
                entriesPerPage={false}
                showTotalEntries={false}
                noEndBorder
            />

            {/* Modal de Edição */}
            <Dialog open={editModalOpen} onClose={() => !isSaving && setEditModalOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Editar Ficha de Honorários</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <MDBox mb={2}>
                                <MDTypography variant="button" fontWeight="regular" color="text">Carteira</MDTypography>
                                <MDSelect
                                    isMulti={false}
                                    getOptionValue={(option) => option.id}
                                    getOptionLabel={(option) => option.nome}
                                    placeholder="Selecione..."
                                    value={carteirasOptions.find(c => c.id === editForm.carteiraId) || null}
                                    options={carteirasOptions}
                                    onChange={(val) => setEditForm({ ...editForm, carteiraId: val ? val.id : "" })}
                                    isDisabled={!isAdminOrGestor}
                                />
                            </MDBox>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <MDBox mb={2}>
                                <MDTypography variant="button" fontWeight="regular" color="text">Status da Ficha</MDTypography>
                                <MDSelect
                                    isMulti={false}
                                    getOptionValue={(option) => option.id}
                                    getOptionLabel={(option) => option.nome}
                                    placeholder="Selecione..."
                                    value={statusOptions.find(s => s.id === editForm.statusId) || null}
                                    options={statusOptions}
                                    onChange={(val) => setEditForm({ ...editForm, statusId: val ? val.id : "" })}
                                    isDisabled={false} // Todos editam
                                />
                            </MDBox>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <MDBox mb={2}>
                                <MDTypography variant="button" fontWeight="regular" color="text">Propriedade (Dono)</MDTypography>
                                <MDSelect
                                    isMulti={false}
                                    getOptionValue={(option) => option.id}
                                    getOptionLabel={(option) => option.nome}
                                    placeholder="Selecione..."
                                    value={usuariosOptions.find(u => u.id === editForm.usuarioId) || null}
                                    options={usuariosOptions}
                                    onChange={(val) => setEditForm({ ...editForm, usuarioId: val ? val.id : "" })}
                                    isDisabled={isOperador}
                                />
                            </MDBox>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditModalOpen(false)} disabled={isSaving}>Cancelar</Button>
                    <MDButton onClick={handleSaveEdit} color="info" disabled={isSaving}>
                        {isSaving ? <CircularProgress size={20} color="inherit" /> : "Salvar Ficha"}
                    </MDButton>
                </DialogActions>
            </Dialog>

            <MDSnackbar
                color="success"
                icon="check"
                title="Sucesso"
                content={sbMessage}
                dateTime=""
                open={successSB}
                onClose={() => setSuccessSB(false)}
                close={() => setSuccessSB(false)}
                bgWhite
            />
            <MDSnackbar
                color="error"
                icon="warning"
                title="Erro"
                content={sbMessage}
                dateTime=""
                open={errorSB}
                onClose={() => setErrorSB(false)}
                close={() => setErrorSB(false)}
                bgWhite
            />
        </MDBox>
    );
}
